import { FullCartQueryResult } from '@graphql/queries/__generated__/cart.generated'
import { PromoCodeFragment } from '@graphql/fragments/__generated__/PromoCode.generated'
import _ from 'lodash'
import { DiscountTypeEnum } from '../../generated/gqlTypes'

type CartLine = FullCartQueryResult['data']['cart']['allLines'][0]
type CartLineOrPart = CartLine | CartLine['supplyParts'][0]

// A supply part's quantity is per ONE kit; the kit line's own quantity is how many kits are in the cart.
const toPricedItems = (lines: CartLine[]): CartLineOrPart[] =>
  lines.flatMap((line): CartLineOrPart[] =>
    line.supplyParts.length
      ? line.supplyParts.map(part => ({ ...part, quantity: part.quantity * line.quantity }))
      : [line]
  )

export const calculateLineTotal = (line: FullCartQueryResult['data']['cart']['allLines'][0]): number => {
  if (!line.supplyParts.length) {
    return line.unitPrice.discount * line.quantity
  }
  return (
    line.supplyParts.reduce((prev, curr) => {
      return prev + curr.unitPrice.discount * curr.quantity
    }, 0) * line.quantity
  )
}

export const calculatePromoCodeApplied = (
  promoCode: PromoCodeFragment,
  lines: FullCartQueryResult['data']['cart']['allLines']
): number => {
  const validCartProductsIds = promoCode.validCartProducts.map(p => p.id)
  const itemSubtotal = toPricedItems(lines)
    .filter(line => validCartProductsIds.includes(line.variant.product.id))
    .reduce((prev, curr) => {
      return prev + curr.unitPrice.discount * curr.quantity
    }, 0)
  if (promoCode.valueType === DiscountTypeEnum.Fixed) {
    return _.max([_.round(itemSubtotal - promoCode.value, 2), 0])
  }
  return _.round((itemSubtotal * promoCode.value) / 100, 2)
}

export const calculateLineWholesaleTotal = (line: FullCartQueryResult['data']['cart']['allLines'][0]): number => {
  if (!line.supplyParts.length) {
    return line.variant.wholesalePrice.undiscount * line.quantity
  }
  return (
    line.supplyParts.reduce((prev, curr) => {
      return prev + curr.variant.wholesalePrice.undiscount * curr.quantity
    }, 0) * line.quantity
  )
}

export const calculateCustomerDiscount = (
  discount: number | null,
  lines: FullCartQueryResult['data']['cart']['allLines']
): number => {
  const itemSubtotal = toPricedItems(lines)
    .filter(line => line.unitPrice.discount === line.unitPrice.undiscount)
    .reduce((prev, curr) => {
      return prev + curr.unitPrice.discount * curr.quantity
    }, 0)
  return _.round((itemSubtotal * discount) / 100, 2)
}
